import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Plus, X, Check, CheckCircle2, RotateCcw, CornerDownRight, ArrowRight } from "lucide-react";
import { dataService } from "../../../../Services/data/data.service";
import { simulationScenarioTypes } from "../../../../Config/constants";
import { processPriceInput, parsePrice } from "../../../../Utils/priceInput";
import { fmt } from "../../../../Utils/formatCurrency";
import { toast } from "react-toastify";
import Button from "../../../Shared/Buttons/Button";
import Chip from "../../../Shared/ui/Chip";
import Input from "../../../Shared/Input";

// ScenarioType: 1 = Accounts, 2 = PriceDefinitions

// ─── helpers ────────────────────────────────────────────────────────────────

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}


function parseLocalNumber(str) {
  if (str == null) return NaN;
  return parseFloat(String(str).replace(/\./g, "").replace(",", "."));
}

// Extract all accounts from profitLossReportData as flat list with current totals
function extractAccounts(profitLossReportData) {
  if (!profitLossReportData) return [];
  const result = [];
  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      if (!cat.accountResults) return;
      cat.accountResults.forEach((acc) => {
        const total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        result.push({
          id: acc.id,
          name: acc.name,
          categoryName: cat.name,
          isRevenue: cat.isRevenue,
          group: cat.isRevenue ? "Revenue" : "Cost",
          currentValue: total,
        });
      });
    });
  };
  processCategories(profitLossReportData.revenueAccountCategoryResults);
  processCategories(profitLossReportData.costAccountCategoryResults);

  const groupOrder = { Revenue: 0, Cost: 1 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    const catCmp = (a.categoryName ?? "").localeCompare(b.categoryName ?? "");
    if (catCmp !== 0) return catCmp;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Extract all price definitions from priceBundles as flat deduped list,
// grouped by Revenue/Cost via P&L data and sorted alphabetically within each group.
function extractPriceDefinitions(priceBundles, profitLossReportData) {
  if (!priceBundles) return [];

  const revenueIds = new Set();
  const costIds = new Set();
  if (profitLossReportData) {
    (profitLossReportData.revenueAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => revenueIds.add(acc.id));
    });
    (profitLossReportData.costAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => costIds.add(acc.id));
    });
  }

  const seen = new Set();
  const result = [];
  priceBundles.forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      const accId = bundle.accountId;
      const group = revenueIds.has(accId) ? "Revenue" : costIds.has(accId) ? "Cost" : "Uncategorized";
      (bundle.items || []).forEach((item) => {
        if (!seen.has(item.priceDefinitionId)) {
          seen.add(item.priceDefinitionId);
          result.push({
            id: item.priceDefinitionId,
            name: item.priceDefinitionName,
            currentValue: item.price,
            uom: item.unitOfMeasurement || null,
            group,
          });
        }
      });
    });
  });

  const groupOrder = { Revenue: 0, Cost: 1, Uncategorized: 2 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Compute projected P&L for accounts-type scenario
function computeAccountsPL(basePLD, items) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const itemMap = {};
  items.forEach((i) => { itemMap[i.itemId] = i.newValue; });

  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        acc.total = itemMap[acc.id] !== undefined ? itemMap[acc.id] : baseTotal;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };

  processCategories(data.revenueAccountCategoryResults);
  processCategories(data.costAccountCategoryResults);

  const revenueTotal = typeof data.revenueTotal === "object"
    ? Object.values(data.revenueTotal)[0] ?? 0
    : data.revenueTotal ?? 0;
  const costTotal = typeof data.costTotal === "object"
    ? Object.values(data.costTotal)[0] ?? 0
    : data.costTotal ?? 0;

  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Compute projected P&L for price-definitions-type scenario
function computePricePL(basePLD, items, allMeasurements, allPriceBundles) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const pdMap = {};
  items.forEach((i) => { pdMap[i.itemId] = i.newValue; });

  if (!allMeasurements || !allPriceBundles) return data;

  // Build per-account ratios
  const accountRatios = {};
  allMeasurements.forEach((m) => {
    if (!m.accountId) return;
    if (!accountRatios[m.accountId]) accountRatios[m.accountId] = { original: 0, projected: 0 };
    accountRatios[m.accountId].original += m.eventPrice || 0;

    if (m.priceBundleId) {
      let bundle = null;
      for (const company of allPriceBundles) {
        const found = (company.priceBundles || []).find((b) => b.id === m.priceBundleId);
        if (found) { bundle = found; break; }
      }
      if (bundle && (bundle.items || []).length > 0) {
        const originalTotal = bundle.items.reduce((s, i) => s + i.price * i.multiplier, 0);
        const projectedTotal = bundle.items.reduce((s, i) => {
          const newP = pdMap[i.priceDefinitionId] !== undefined ? pdMap[i.priceDefinitionId] : i.price;
          return s + newP * i.multiplier;
        }, 0);
        accountRatios[m.accountId].projected += originalTotal > 0
          ? (m.eventPrice || 0) * (projectedTotal / originalTotal)
          : (m.eventPrice || 0);
      } else {
        accountRatios[m.accountId].projected += m.eventPrice || 0;
      }
    } else {
      accountRatios[m.accountId].projected += m.eventPrice || 0;
    }
  });

  const applyRatios = (cats) => {
    (cats || []).forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        const ratio = accountRatios[acc.id];
        acc.total = ratio && ratio.original > 0 ? baseTotal * (ratio.projected / ratio.original) : baseTotal;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };

  applyRatios(data.revenueAccountCategoryResults);
  applyRatios(data.costAccountCategoryResults);
  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

function computeScenarioPL(basePLD, scenario, allMeasurements, allPriceBundles) {
  if (!scenario) return null;
  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return computeAccountsPL(basePLD, scenario.items || []);
  }
  return computePricePL(basePLD, scenario.items || [], allMeasurements, allPriceBundles);
}

function getBaselinePL(profitLossReportData) {
  if (!profitLossReportData || Array.isArray(profitLossReportData) || !profitLossReportData.revenueAccountCategoryResults) return null;
  const data = deepClone(profitLossReportData);

  const normalise = (cats) => {
    (cats || []).forEach((cat) => {
      const catT = typeof cat.total === "object" ? Object.values(cat.total)[0] ?? 0 : cat.total ?? 0;
      cat.total = catT;
      (cat.accountResults || []).forEach((acc) => {
        acc.total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
      });
    });
  };
  normalise(data.revenueAccountCategoryResults);
  normalise(data.costAccountCategoryResults);

  data.revenueTotal = typeof data.revenueTotal === "object" ? Object.values(data.revenueTotal)[0] ?? 0 : data.revenueTotal ?? 0;
  data.costTotal = typeof data.costTotal === "object" ? Object.values(data.costTotal)[0] ?? 0 : data.costTotal ?? 0;
  data.totalProfit = typeof data.totalProfit === "object" ? Object.values(data.totalProfit)[0] ?? 0 : data.totalProfit ?? 0;
  return data;
}

// Build the projection object (format consumed by ProfitLossReport / InsightsTab)
// from the current paramRows of a scenario.
function buildProjection(scenario, paramRows) {
  if (!scenario || scenario.isBaseline) return false;
  const changed = paramRows.filter((p) => Math.abs(p.editVal - p.currentValue) > 0.001);
  if (changed.length === 0) return false;

  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return {
      accounts: changed.map((p) => ({
        id: p.id,
        name: p.name,
        isRevenue: p.isRevenue,
        percentage: p.currentValue !== 0 ? (p.editVal / p.currentValue) * 100 : 100,
      })),
    };
  }
  return {
    priceDefinitions: changed.map((p) => ({
      priceDefinitionId: p.id,
      priceDefinitionName: p.name,
      currentPrice: p.currentValue,
      newPrice: p.editVal,
    })),
  };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function NewScenarioModal({ open, onConfirm, onCancel }) {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [type, setType] = useState(simulationScenarioTypes.ACCOUNTS);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setType(simulationScenarioTypes.ACCOUNTS);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  return (
    <Dialog open={open} maxWidth={false} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>New scenario</DialogTitle>
        <button className={classes.dialog__closeButton} onClick={onCancel}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          <p className={classes.dialog__sub}>Give the scenario a name and choose which parameters to adjust.</p>
          <div className={classes.inputContainer}> 
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>Name</label>
            </div>
            <Input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Energy +50%, Machine replacement…"
              onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name.trim(), type); }}
              fullwidth
            />
          </div>
          <div className={classes.inputContainer} style={{ marginTop: 16 }}>
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>Parameter type</label>
            </div>
            <div className={classes.typeRow}>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.ACCOUNTS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.ACCOUNTS)}
              >
                Accounts
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.PRICE_DEFINITIONS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.PRICE_DEFINITIONS)}
              >
                Price definitions
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button disabled={!name.trim()} onClick={() => onConfirm(name.trim(), type)}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function ParamRow({ param, editValue, onChange, onReset, disabled, currency, decimals = 0, uom, showCategory = true }) {
  const classes = useStyles();
  const currentVal = param.currentValue ?? 0;
  const scenarioVal = editValue !== undefined ? editValue : currentVal;
  const isChanged = Math.abs(scenarioVal - currentVal) > 0.001;
  const pct = currentVal !== 0 ? ((scenarioVal - currentVal) / Math.abs(currentVal)) * 100 : 0;
  const pctDisplay = pct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const isRevenue = param.isRevenue === true || param.group === "Revenue";
  const isCost = param.isRevenue === false || param.group === "Cost";
  const chipVariant = !isChanged ? "default" : isRevenue ? (pct > 0 ? "success" : "danger") : (pct > 0 ? "danger" : "success");

  const formatNum = (n) => n.toLocaleString("nl-BE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const [displayVal, setDisplayVal] = useState(() => formatNum(scenarioVal));
  // Track the last value we propagated up so we can distinguish external prop changes from our own
  const lastPropagatedRef = useRef(scenarioVal);
  const inputRef = useRef(null);
  const cursorPosRef = useRef(null);

  useEffect(() => {
    if (Math.abs(scenarioVal - lastPropagatedRef.current) > 0.0001) {
      lastPropagatedRef.current = scenarioVal;
      setDisplayVal(formatNum(scenarioVal));
    }
  }, [scenarioVal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore cursor after React re-renders the input with the new formatted value
  useLayoutEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      cursorPosRef.current = null;
    }
  });

  const handleChange = (e) => {
    const raw = e.target.value;
    const cursorPos = e.target.selectionStart;

    const formatted = processPriceInput(raw, 2);
    if (formatted === null) return; // invalid char — ignore, keep current display

    // Remap cursor: count significant chars (digits + comma) before cursor in raw,
    // then find the same count in the formatted string (dots are separators and shift positions).
    const sigBefore = (raw.slice(0, cursorPos).match(/[\d,]/g) || []).length;
    let newCursorPos = formatted.length;
    if (sigBefore === 0) {
      newCursorPos = 0;
    } else {
      let sigCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/[\d,]/.test(formatted[i])) {
          if (++sigCount === sigBefore) { newCursorPos = i + 1; break; }
        }
      }
    }
    cursorPosRef.current = newCursorPos;

    setDisplayVal(formatted);
    const parsed = parsePrice(formatted);
    const numVal = parsed !== null ? parsed : 0;
    lastPropagatedRef.current = numVal;
    onChange(param.id, numVal);
  };

  const handleBlur = () => {
    // Reformat with thousands separators on blur
    const parsed = parsePrice(displayVal);
    if (parsed !== null) setDisplayVal(formatNum(parsed));
  };

  return (
    <tr className={classes.paramRow}>
      <td className={classes.paramCell}>
        <div className={classes.param__name}>{param.name}</div>
        {showCategory && param.categoryName && <div className={classes.param__cat}>{param.categoryName}</div>}
      </td>
      <td className={`${classes.paramCell} ${classes.paramCell_mono}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span>{fmt(currentVal, currency, decimals)}</span>
          {uom && <Chip variant="default" size="extraSmall">{uom}</Chip>}
        </div>
      </td>
      <td className={classes.paramCell}>
        <div className={classes.param__inputWrap}>
          <span className={classes.param__unit}>{currency?.symbol ?? "€"}</span>
          <input
            ref={inputRef}
            className={`${classes.param__input} ${isChanged ? classes.param__input_changed : ""}`}
            value={displayVal}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {isChanged && (
            <button
              className={classes.param__resetBtn}
              onClick={() => onReset && onReset(param.id)}
              title="Reset to original value"
              tabIndex={-1}
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </td>
      <td className={classes.paramCell}>
        <Chip
          variant={chipVariant}
          size="extraSmall"
        >
          {!isChanged ? "—" : (pct > 0 ? "+" : "") + pctDisplay + "%"}
        </Chip>
      </td>
    </tr>
  );
}

function PLTable({ baseline, scenarios, activeScenario, compareScenarios, currency }) {
  const classes = useStyles();
  if (!baseline) return null;

  const activePL = activeScenario?._pl ?? baseline;
  const comparePLs = compareScenarios.map((s) => s._pl ?? baseline);
  const showCompare = comparePLs.length > 0;

  const allRevenueCats = baseline.revenueAccountCategoryResults || [];
  const allCostCats = baseline.costAccountCategoryResults || [];

  const deltaCell = (baseVal, scenVal) => {
    const diff = scenVal - baseVal;
    if (Math.abs(diff) < 1) return <td className={`${classes.plCell} ${classes.plCell_delta}`}>—</td>;
    const cls = diff < 0 ? classes.plVal_neg : classes.plVal_pos;
    return (
      <td className={`${classes.plCell} ${classes.plCell_delta} ${cls}`}>
        {diff > 0 ? "+" : ""}{fmt(diff, currency, 2)}
      </td>
    );
  };

  const valCell = (val, baseVal, isSection, isTotal, isRevenue = true) => {
    const changed = Math.abs(val - baseVal) > 1;
    const betterClass = isRevenue ? classes.plVal_pos : classes.plVal_neg;
    const worseClass = isRevenue ? classes.plVal_neg : classes.plVal_pos;
    const cls = changed ? (val > baseVal ? betterClass : worseClass) : "";
    return (
      <td className={`${classes.plCell} ${isSection ? classes.plCell_section : ""} ${isTotal ? classes.plCell_total : ""} ${cls}`}>
        {fmt(val, currency, 2)}
      </td>
    );
  };

  const renderSection = (label, cats, activePL_cats, comparePL_cats_arr, isRevenue) => {
    const baseCats = ((isRevenue ? baseline.revenueAccountCategoryResults : baseline.costAccountCategoryResults) || [])
      .slice()
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const activeCats = activePL_cats || [];
    const colSpan = 2 + comparePLs.length + (showCompare ? 1 : 0);

    const rows = [
      <tr key={label} className={classes.plSectionRow}>
        <td className={`${classes.plCell} ${classes.plCell_section} ${isRevenue ? classes.plCell_section_revenue : classes.plCell_section_cost}`} colSpan={colSpan}>
          {label}
        </td>
      </tr>,
    ];

    baseCats.forEach((baseCat) => {
      const activeCat = activeCats.find((c) => c.id === baseCat.id) || baseCat;
      const baseT = typeof baseCat.total === "object" ? Object.values(baseCat.total)[0] ?? 0 : baseCat.total ?? 0;
      const activeT = typeof activeCat.total === "object" ? Object.values(activeCat.total)[0] ?? 0 : activeCat.total ?? 0;

      // Category sub-header row
      rows.push(
        <tr key={baseCat.id} className={classes.plCategoryRow}>
          <td className={`${classes.plCell} ${classes.plCell_category}`}>{baseCat.name}</td>
          {valCell(activeT, baseT, false, false, isRevenue)}
          {showCompare && comparePL_cats_arr.map((compareCats, ci) => {
            const compareCat = compareCats.find((c) => c.id === baseCat.id) || baseCat;
            const compareT = typeof compareCat.total === "object" ? Object.values(compareCat.total)[0] ?? 0 : compareCat.total ?? 0;
            return valCell(compareT, baseT, false, false, isRevenue);
          })}
          {showCompare && deltaCell(baseT, Math.max(activeT, ...comparePL_cats_arr.map((cc) => {
            const c = cc.find((c) => c.id === baseCat.id) || baseCat;
            return typeof c.total === "object" ? Object.values(c.total)[0] ?? 0 : c.total ?? 0;
          })))}
        </tr>
      );

      // Individual account rows
      (baseCat.accountResults || []).slice().sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")).forEach((baseAcc) => {
        const activeAcc = (activeCat.accountResults || []).find((a) => a.id === baseAcc.id) || baseAcc;
        const baseAccT = typeof baseAcc.total === "object" ? Object.values(baseAcc.total)[0] ?? 0 : baseAcc.total ?? 0;
        const activeAccT = typeof activeAcc.total === "object" ? Object.values(activeAcc.total)[0] ?? 0 : activeAcc.total ?? 0;

        rows.push(
          <tr key={`${baseCat.id}-${baseAcc.id}`} className={classes.plRow}>
            <td className={`${classes.plCell} ${classes.plCell_account}`}><span className={classes.plCell_account_inner}><ArrowRight className={classes.plCell_account_icon} size={12} />{baseAcc.name}</span></td>
            {valCell(activeAccT, baseAccT, false, false, isRevenue)}
            {showCompare && comparePL_cats_arr.map((compareCats) => {
              const compareCat = compareCats.find((c) => c.id === baseCat.id) || baseCat;
              const compareAcc = (compareCat.accountResults || []).find((a) => a.id === baseAcc.id) || baseAcc;
              const compareAccT = typeof compareAcc.total === "object" ? Object.values(compareAcc.total)[0] ?? 0 : compareAcc.total ?? 0;
              return valCell(compareAccT, baseAccT, false, false, isRevenue);
            })}
            {showCompare && deltaCell(baseAccT, Math.max(activeAccT, ...comparePL_cats_arr.map((cc) => {
              const compareCat = cc.find((c) => c.id === baseCat.id) || baseCat;
              const compareAcc = (compareCat.accountResults || []).find((a) => a.id === baseAcc.id) || baseAcc;
              return typeof compareAcc.total === "object" ? Object.values(compareAcc.total)[0] ?? 0 : compareAcc.total ?? 0;
            })))}
          </tr>
        );
      });
    });

    return rows;
  };

  const baseRevT = baseline.revenueTotal ?? 0;
  const baseCostT = baseline.costTotal ?? 0;
  const baseProfit = baseline.totalProfit ?? 0;
  const activeRevT = activePL.revenueTotal ?? 0;
  const activeCostT = activePL.costTotal ?? 0;
  const activeProfit = activePL.totalProfit ?? 0;

  return (
    <table className={`${classes.plTable} no-stripes`}>
      <thead>
        <tr>
          <th className={classes.plTh} style={{ textAlign: "left" }}>P&amp;L Component</th>
          <th className={`${classes.plTh} ${classes.plTh_scenario}`}>{activeScenario?.name ?? "Baseline"}</th>
          {showCompare && compareScenarios.map((s) => (
            <th key={s.id} className={`${classes.plTh} ${classes.plTh_compare}`}>{s.name}</th>
          ))}
          {showCompare && <th className={`${classes.plTh} ${classes.plTh_delta}`}>Delta best</th>}
        </tr>
      </thead>
      <tbody>
        {renderSection(
          "Revenue",
          baseline.revenueAccountCategoryResults,
          activePL.revenueAccountCategoryResults || [],
          comparePLs.map((p) => p.revenueAccountCategoryResults || []),
          true
        )}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>Total Revenue</strong></td>
          {valCell(activeRevT, baseRevT, false, false, true)}
          {showCompare && comparePLs.map((p, i) => valCell(p.revenueTotal ?? 0, baseRevT, false, false, true))}
          {showCompare && deltaCell(baseRevT, Math.max(activeRevT, ...comparePLs.map((p) => p.revenueTotal ?? 0)))}
        </tr>

        {renderSection(
          "Costs",
          baseline.costAccountCategoryResults,
          activePL.costAccountCategoryResults || [],
          comparePLs.map((p) => p.costAccountCategoryResults || []),
          false
        )}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>Total Costs</strong></td>
          {valCell(activeCostT, baseCostT, false, false, false)}
          {showCompare && comparePLs.map((p, i) => valCell(p.costTotal ?? 0, baseCostT, false, false, false))}
          {showCompare && deltaCell(baseCostT, Math.max(activeCostT, ...comparePLs.map((p) => p.costTotal ?? 0)))}
        </tr>

        <tr className={classes.plTotalRow}>
          <td className={`${classes.plCell} ${classes.plCell_total}`}>Gross Profit</td>
          {valCell(activeProfit, baseProfit, false, true)}
          {showCompare && comparePLs.map((p, i) => valCell(p.totalProfit ?? 0, baseProfit, false, true))}
          {showCompare && deltaCell(baseProfit, Math.max(activeProfit, ...comparePLs.map((p) => p.totalProfit ?? 0)))}
        </tr>
      </tbody>
    </table>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ProjectionsTab({
  graphId,
  profitLossReportData,
  allPriceBundles,
  allMeasurements,
  currency,
  login,
  activeProjection,
  handleSetActiveProjection,
  // Lifted state — survives tab switches
  selectedScenarioId,
  onSelectedScenarioChange,
  appliedScenarioId,
  onAppliedScenarioChange,
}) {
  const classes = useStyles();

  // Saved scenarios loaded from backend (no baseline here)
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);

  const activeScenarioId = selectedScenarioId;
  const setActiveScenarioId = onSelectedScenarioChange;

  const [compareIds, setCompareIds] = useState([]);

  // Unsaved param edits { itemId: newValue }
  const [editParams, setEditParams] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync: if activeProjection was cleared externally, clear the applied tracker
  useEffect(() => {
    if (!activeProjection) onAppliedScenarioChange(null);
  }, [activeProjection]); // eslint-disable-line react-hooks/exhaustive-deps

  const baselinePL = getBaselinePL(profitLossReportData);

  const allParams = activeScenarioId === "baseline" || !savedScenarios.find((s) => s.id === activeScenarioId)
    ? []
    : (() => {
        const s = savedScenarios.find((sc) => sc.id === activeScenarioId);
        if (s.scenarioType === simulationScenarioTypes.ACCOUNTS) return extractAccounts(profitLossReportData);
        return extractPriceDefinitions(allPriceBundles, profitLossReportData);
      })();

  // Build scenario list: baseline + saved
  const scenarioList = [
    {
      id: "baseline",
      name: "Current (baseline)",
      isBaseline: true,
      scenarioType: simulationScenarioTypes.ACCOUNTS,
      items: [],
      _pl: baselinePL,
    },
    ...savedScenarios.map((s) => ({
      ...s,
      _pl: computeScenarioPL(baselinePL, s, allMeasurements, allPriceBundles),
    })),
  ];

  // Compute active scenario's "preview" P&L (incorporates unsaved edits)
  const activeScenario = scenarioList.find((s) => s.id === activeScenarioId) ?? scenarioList[0];
  const activeScenarioWithEdits = {
    ...activeScenario,
    items: allParams.map((p) => ({
      itemId: p.id,
      newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
    })),
  };
  const activePL = activeScenario.isBaseline
    ? baselinePL
    : computeScenarioPL(baselinePL, activeScenarioWithEdits, allMeasurements, allPriceBundles);

  const compareScenarios = compareIds
    .map((id) => scenarioList.find((s) => s.id === id))
    .filter(Boolean);

  // ── data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!graphId) return;
    setLoadingScenarios(true);
    dataService
      .getGraphSimulationScenarios(graphId)
      .then((data) => {
        setSavedScenarios(Array.isArray(data) ? data.map((s) => ({
          ...s,
          scenarioType: s.scenarioType,
          items: (s.items || []).map((i) => ({ itemId: i.itemId, newValue: Number(i.newValue) })),
        })) : []);
      })
      .catch(() => setSavedScenarios([]))
      .finally(() => setLoadingScenarios(false));
  }, [graphId]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleSelectScenario = (id) => {
    setActiveScenarioId(id);
    setEditParams({});
  };

  const handleUpdateParam = (paramId, value) => {
    setEditParams((prev) => ({ ...prev, [paramId]: value }));
  };

  const handleResetEdits = () => setEditParams({});

  const handleResetParam = (paramId) => {
    const param = allParams.find((p) => p.id === paramId);
    if (!param) return;
    setEditParams((prev) => ({ ...prev, [paramId]: param.currentValue }));
  };

  const handleSave = async () => {
    if (activeScenario.isBaseline) return;
    setSaving(true);
    try {
      const items = allParams.map((p) => ({
        itemId: p.id,
        newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
      }));
      const result = await dataService.updateGraphSimulationScenario(
        activeScenario.id,
        activeScenario.name,
        items,
        login?.userid ?? 0
      );
      if (result?.success !== false) {
        setSavedScenarios((prev) =>
          prev.map((s) =>
            s.id === activeScenario.id ? { ...s, items } : s
          )
        );
        setEditParams({});
        toast.success("Scenario saved", { autoClose: 2000 });
      } else {
        toast.error(result.message || "Save failed", { autoClose: 3000 });
      }
    } catch {
      toast.error("Save failed", { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleNewScenario = async (name, type) => {
    setModalOpen(false);
    try {
      const result = await dataService.createGraphSimulationScenario(
        graphId,
        name,
        type,
        login?.userid ?? 0
      );
      if (result?.success !== false && result?.scenarioId) {
        const newScenario = { id: result.scenarioId, name, scenarioType: type, items: [] };
        setSavedScenarios((prev) => [...prev, newScenario]);
        setActiveScenarioId(result.scenarioId);
        setEditParams({});
      } else {
        toast.error(result?.message || "Could not create scenario", { autoClose: 3000 });
      }
    } catch {
      toast.error("Could not create scenario", { autoClose: 3000 });
    }
  };

  const handleDeleteScenario = async (id) => {
    try {
      await dataService.deleteGraphSimulationScenario(id, login?.userid ?? 0);
      setSavedScenarios((prev) => prev.filter((s) => s.id !== id));
      setCompareIds((prev) => prev.filter((x) => x !== id));
      if (activeScenarioId === id) setActiveScenarioId("baseline");
    } catch {
      toast.error("Could not delete scenario", { autoClose: 3000 });
    }
  };

  const handleApply = () => {
    const projection = buildProjection(activeScenario, paramRows);
    if (!projection) {
      toast.info("No changes to apply — parameters match the baseline.", { autoClose: 3000 });
      return;
    }
    handleSetActiveProjection(projection);
    onAppliedScenarioChange(activeScenarioId);
  };

  const handleClearProjection = () => {
    handleSetActiveProjection(null);
    onAppliedScenarioChange(null);
  };

  const handleToggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const hasUnsavedEdits = Object.keys(editParams).length > 0;

  // ── param list for active scenario ────────────────────────────────────────

  const paramRows = allParams.map((p) => {
    const savedItem = activeScenario.items.find((i) => i.itemId === p.id);
    const editVal = editParams[p.id] !== undefined ? editParams[p.id] : (savedItem?.newValue ?? p.currentValue);
    return { ...p, editVal };
  });

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className={classes.root}>
      {/* SIDEBAR */}
      <div className={classes.sidebar}>
        <div className={classes.sidebar__title}>Scenarios</div>

        {loadingScenarios ? (
          <div className={classes.sidebar__loading}>Loading…</div>
        ) : (
          scenarioList.map((s) => {
            const impact = s.isBaseline ? null : ((s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0));
            const isActive = s.id === activeScenarioId;
            const isComparing = compareIds.includes(s.id);
            return (
              <div
                key={s.id}
                className={`${classes.scenarioCard} ${isActive ? classes.scenarioCard_active : ""} ${isComparing && !isActive ? classes.scenarioCard_comparing : ""}`}
                onClick={() => handleSelectScenario(s.id)}
              >
                <div className={classes.scenarioCard__nameRow}>
                  <span className={classes.scenarioCard__name}>{s.name}</span>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {!s.isBaseline && appliedScenarioId === s.id && (
                      <span className={classes.scenarioCard__appliedBadge}>Active</span>
                    )}
                    {s.isBaseline && <span className={classes.scenarioCard__tag}>baseline</span>}
                  </div>
                </div>
                <div className={classes.scenarioCard__summary}>
                  {s.isBaseline
                    ? `Gross profit: ${fmt(baselinePL?.totalProfit ?? 0, currency)}`
                    : impact != null
                    ? `Impact: ${impact >= 0 ? "+" : ""}${fmt(impact, currency)}`
                    : "—"}
                </div>
                {!s.isBaseline && (
                  <div className={classes.scenarioCard__actions} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="extraSmall"
                      variant={isComparing ? "outlined" : "secondary"}
                      onClick={() => handleToggleCompare(s.id)}
                    >
                      {isComparing ? "✓ Compare" : "Compare"}
                    </Button>
                    <Button
                      size="extraSmall"
                      variant="destructive"
                      onClick={() => handleDeleteScenario(s.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}

        <div className={classes.addScenario} onClick={() => setModalOpen(true)}>
          <Plus size={14} style={{ marginRight: 4 }} />
          New scenario
        </div>

        {compareIds.length > 0 && (
          <div className={classes.compareBar}>
            <div className={classes.compareBar__title}>Comparing</div>
            {compareIds.map((id) => {
              const s = scenarioList.find((x) => x.id === id);
              if (!s) return null;
              const impact = (s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0);
              return (
                <div key={id} className={classes.compareBar__item}>
                  <span>{s.name}</span>
                  <span style={{ color: impact >= 0 ? "#4ade80" : "#f87171" }}>
                    {impact >= 0 ? "+" : ""}{fmt(impact, currency)}
                  </span>
                </div>
              );
            })}
            <button className={classes.compareBar__clear} onClick={() => setCompareIds([])}>Clear</button>
          </div>
        )}
      </div>

      {/* MAIN */}
      <div className={classes.main}>
        {/* ACTIVE PROJECTION INDICATOR */}
        {activeProjection && appliedScenarioId && (
          <div className={classes.projectionBanner}>
            <div className={classes.projectionBanner__left}>
              <CheckCircle2 size={15} className={classes.projectionBanner__icon} />
              <span>
                Simulation active
                {scenarioList.find((s) => s.id === appliedScenarioId) && (
                  <> — <strong>{scenarioList.find((s) => s.id === appliedScenarioId)?.name}</strong></>
                )}
              </span>
            </div>
            <Button variant="secondary" size="small" onClick={handleClearProjection}>Clear</Button>
          </div>
        )}
        {/* PARAMETER EDITOR */}
        <div className={classes.editorCard}>
          <div className={classes.editorCard__head}>
            <div>
              <div className={classes.editorCard__title}>
                Parameters — {activeScenario.name}
                {!activeScenario.isBaseline && (
                  <span className={classes.typeChip}>
                    {activeScenario.scenarioType === simulationScenarioTypes.ACCOUNTS ? "Accounts" : "Price definitions"}
                  </span>
                )}
              </div>
              <div className={classes.editorCard__sub}>
                {activeScenario.isBaseline
                  ? "Baseline — select a scenario to adjust parameters"
                  : "Adjust parameters · P&L updates automatically"}
              </div>
            </div>
          </div>
          <table className={`${classes.paramTable} no-stripes`}>
            <thead>
              <tr>
                <th className={classes.paramTh}>Parameter</th>
                <th className={classes.paramTh}>Current</th>
                <th className={classes.paramTh}>Scenario</th>
                <th className={classes.paramTh}>Delta</th>
              </tr>
            </thead>
            <tbody>
              {activeScenario.isBaseline && (
                <tr>
                  <td colSpan={4} className={classes.paramEmpty}>
                    Select a scenario from the sidebar to edit parameters.
                  </td>
                </tr>
              )}
              {!activeScenario.isBaseline && paramRows.length === 0 && (
                <tr>
                  <td colSpan={4} className={classes.paramEmpty}>
                    No parameters available.
                  </td>
                </tr>
              )}
              {!activeScenario.isBaseline && (() => {
                const isPriceDef = activeScenario.scenarioType === simulationScenarioTypes.PRICE_DEFINITIONS;
                const hasMultipleGroups = new Set(paramRows.map((p) => p.group)).size > 1;
                let lastGroup = null;
                let lastCategory = null;
                return paramRows.flatMap((p) => {
                  const rows = [];
                  if (p.group && (hasMultipleGroups || (isPriceDef && p.group !== "Uncategorized")) && p.group !== lastGroup) {
                    lastGroup = p.group;
                    lastCategory = null;
                    rows.push(
                      <tr key={`group-${p.group}`} className={classes.paramGroupRow}>
                        <td colSpan={4} className={`${classes.paramGroupCell} ${p.group === "Revenue" ? classes.paramGroupCell_revenue : p.group === "Cost" ? classes.paramGroupCell_cost : ""}`}>{p.group}</td>
                      </tr>
                    );
                  }
                  if (!isPriceDef && p.categoryName && p.categoryName !== lastCategory) {
                    lastCategory = p.categoryName;
                    rows.push(
                      <tr key={`cat-${p.group}-${p.categoryName}`} className={classes.paramCategoryRow}>
                        <td colSpan={4} className={classes.paramCategoryCell}>{p.categoryName}</td>
                      </tr>
                    );
                  }
                  rows.push(
                    <ParamRow
                      key={p.id}
                      param={p}
                      editValue={p.editVal}
                      onChange={handleUpdateParam}
                      onReset={handleResetParam}
                      disabled={false}
                      currency={currency}
                      decimals={isPriceDef ? 2 : 0}
                      uom={isPriceDef ? p.uom : undefined}
                      showCategory={false}
                    />
                  );
                  return rows;
                });
              })()}
            </tbody>
          </table>
          {!activeScenario.isBaseline && (
            <div className={classes.editorCard__footer}>
              <Button variant="ghost" size="small" onClick={handleResetEdits} disabled={!hasUnsavedEdits}>
                Reset
              </Button>
              <div className={classes.editorCard__footerRight}>
                <Button
                  className={classes.applyBtn}
                  onClick={handleApply}
                  startIcon={appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits ? <Check /> : null}
                >
                  {appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits
                    ? "Applied"
                    : "Apply to all tabs"}
                </Button>
                <Button variant="primary" loading={saving} onClick={handleSave}>
                  Save scenario
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* P&L IMPACT TABLE */}
        <div className={classes.plCard}>
          <div className={classes.plCard__head}>
            <div className={classes.plCard__title}>P&amp;L Statement</div>
          </div>
          <div className={classes.plCard__tableWrap}>
            <PLTable
              baseline={baselinePL}
              scenarios={scenarioList}
              activeScenario={{ ...activeScenario, _pl: activePL }}
              compareScenarios={compareScenarios}
              currency={currency}
            />
          </div>
        </div>
      </div>

      <NewScenarioModal
        open={modalOpen}
        onConfirm={handleNewScenario}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: 500,
    gap: 0,
  },

  // Sidebar
  sidebar: {
    width: 260,
    flexShrink: 0,
    background: "#fff",
    borderRight: "1px solid var(--color-border-light)",
    paddingRight: "16px",
    alignSelf: "flex-start",
    position: "sticky",
    top: 76,
  },
  sidebar__title: {
    fontSize: 12,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "var(--color-text-secondary)",
    marginBottom: 12,
  },
  sidebar__loading: {
    fontSize: 13,
    color: "#9ca3af",
    padding: "8px 0",
  },

  // Scenario cards
  scenarioCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: "#c8922a" },
  },
  scenarioCard_active: {
    borderColor: "#c8922a",
    background: "#fdf5e8",
  },
  scenarioCard_comparing: {
    borderColor: "#1a56db",
    background: "#e8f0fd",
  },
  scenarioCard__nameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 3,
  },
  scenarioCard__name: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  scenarioCard__tag: {
    fontSize: 10,
    color: "#9ca3af",
  },
  scenarioCard__appliedBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#15803d",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 4,
    padding: "1px 6px",
  },
  scenarioCard__summary: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  scenarioCard__actions: {
    display: "flex",
    gap: 4,
    marginTop: 6,
  },

  addScenario: {
    border: "1px dashed #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    textAlign: "center",
    fontSize: 14,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    transition: "all 0.15s",
    "&:hover": { borderColor: "#c8922a", color: "#c8922a" },
  },

  compareBar: {
    background: "#07142d",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
  },
  compareBar__title: {
    fontSize: 11,
    color: "rgba(255,255,255,0.6)",
    marginBottom: 6,
  },
  compareBar__item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#fff",
    marginBottom: 3,
  },
  compareBar__clear: {
    fontSize: 11,
    color: "rgba(255,255,255,0.4)",
    cursor: "pointer",
    background: "none",
    border: "none",
    textDecoration: "underline",
    marginTop: 6,
    fontFamily: "inherit",
    "&:hover": { color: "#fff" },
  },

  // Main area
  main: {
    flex: 1,
    minWidth: 0,
    paddingLeft: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  // Editor card
  editorCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  editorCard__head: {
    padding: "16px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  editorCard__sub: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 2,
  },
  typeChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    background: "#fdf5e8",
    border: "1px solid #c8922a",
    color: "#c8922a",
  },

  // Param table
  paramTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  paramTh: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    padding: "8px 16px",
    textAlign: "left",
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
  },
  paramRow: {
    "&:hover $paramCell": { background: "#f9fafb" },
    "&:last-child $paramCell": { borderBottom: "none" },
  },
  paramCell: {
    padding: "8px 16px",
    borderBottom: "1px solid #DCDCDC",
    verticalAlign: "middle",
  },
  paramCell_mono: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 14,
    color: "var(--color-text-secondary)",
  },
  param__name: {
    fontSize: 13,
    color: "var(--color-text-primary)",
  },
  param__cat: {
    fontSize: 11,
    color: "var(--color-text-secondary)",
  },
  param__inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  param__unit: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
  param__input: {
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 14,
    width: 128,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: "var(--color-primary-light)" },
  },
  param__input_changed: {
    borderColor: "var(--color-primary-light)",
    background: "var(--color-primary-tint)",
  },
  param__resetBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f9fafb",
    color: "#9ca3af",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "#c8922a",
      color: "#c8922a",
      background: "#fdf5e8",
    },
  },
  editorCard__footer: {
    padding: "16px",
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__footerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  applyBtn: {
    "&&": {
      background: "#07142d",
      color: "#fff",
      fontWeight: 500,
    },
    "&&:hover": { opacity: 0.85 },
  },
  // Active projection indicator banner
  projectionBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 10,
    padding: "10px 16px",
  },
  projectionBanner__left: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#15803d",
  },
  projectionBanner__icon: {
    color: "#16a34a",
    flexShrink: 0,
  },
  paramGroupRow: {
    background: "#f9fafb",
  },
  paramGroupCell: {
    padding: "6px 16px",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    borderBottom: "1px solid #DCDCDC",
    borderTop: "1px solid #DCDCDC",
  },
  paramGroupCell_revenue: { color: "#16a34a" },
  paramGroupCell_cost: { color: "#dc2626" },
  paramCategoryRow: {
    background: "#fff",
  },
  paramCategoryCell: {
    padding: "5px 20px",
    fontSize: 11,
    fontWeight: 600,
    color: "#374151",
    borderBottom: "1px solid #DCDCDC",
  },
  paramEmpty: {
    padding: "20px 16px",
    fontSize: 13,
    color: "#9ca3af",
    textAlign: "center",
  },

  // P&L card
  plCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  plCard__head: {
    padding: "14px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  plCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  plCard__tableWrap: {
    overflowX: "auto",
  },

  // P&L table
  plTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  plTh: {
    padding: "8px 14px",
    textAlign: "right",
    fontSize: 11,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
  },
  plTh_scenario: {
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  plTh_compare: {
    background: "#1a56db",
    color: "#fff",
    fontWeight: 500,
  },
  plTh_delta: {
    background: "rgba(0,0,0,0.02)",
    color: "var(--color-text-secondary)",
  },
  plRow: {
    "&:hover $plCell": { background: "#f9fafb" },
  },
  plSectionRow: {
    background: "#fff",
  },
  plSubtotalRow: {
    "& td": { background: "var(--color-bg-page) !important" },
  },
  plTotalRow: {
    background: "var(--color-bg-card)",
    "& $plCell": {
      borderTop: "2px solid #c8922a",
    },
  },
  plCell: {
    padding: "8px 14px",
    borderBottom: "1px solid #DCDCDC",
    textAlign: "right",
    fontFamily: "'DM Mono', 'Courier New', monospace",
    "&:first-child": {
      textAlign: "left",
      fontFamily: "inherit",
      color: "#111827",
    },
  },
  plCell_section: {
    fontWeight: 600,
    fontSize: 12,
    color: "#07142d",
  },
  plCell_section_revenue: { color: "#16a34a !important" },
  plCell_section_cost: { color: "#dc2626 !important" },
  plCell_total: {
    fontWeight: 600,
    fontFamily: "inherit",
    fontSize: 14,
    color: "#c8922a",
  },
  plCell_delta: {
    background: "rgba(0,0,0,0.02)",
  },
  plCategoryRow: {
    "&:hover $plCell": { background: "#f3f4f6" },
  },
  plCell_category: {
    fontWeight: 600,
    fontSize: 14,
    color: "#374151",
  },
  plCell_account: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    paddingLeft: 16,
  },
  plCell_account_inner: {
    display: "inline-flex",
    alignItems: "center",
  },
  plCell_account_icon: {
    marginRight: 6,
    color: "var(--color-text-secondary)",
  },
  plVal_pos: { color: "var(--color-success)", fontWeight: 500 },
  plVal_neg: { color: "var(--color-error)", fontWeight: 500 },

  // Dialog (matches PriceDefinitionDialog design system)
  dialogPaper: {
    width: "400px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
  },
  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },
  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    padding: "0px !important",
  },
  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: "3px !important",
    height: "25px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },
  },
  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  dialog__content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
  },
  dialog__sub: {
    fontSize: "14px",
    color: "#6b7280",
    margin: "0 0 16px 0",
  },
  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  labelText: {
    fontSize: "14px",
    color: "var(--color-text-primary)",
    opacity: 0.8,
  },
  typeRow: {
    display: "flex",
    gap: "8px",
  },
  typeBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #EDEDED",
    background: "#F8F8F8",
    fontSize: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#6b7280",
    transition: "all 0.15s",
    "&:hover": { borderColor: "var(--color-text-primary)" },
  },
  typeBtn_active: {
    borderColor: "var(--color-text-primary)",
    background: "#fff",
    color: "var(--color-text-primary)",
    fontWeight: 500,
  },
}));
